import { computed, action, makeObservable } from 'mobx'
import { BehaviorSubject, combineLatest, defer, firstValueFrom } from 'rxjs'
import { task } from 'mobx-task'
import { AlertState } from 'alerts/Alert'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { ViewModelList } from 'infra/ViewModelList'
import {
  AdminPayoutHistoryStore,
  ListHistoricalTransactionsQuery,
} from '../AdminPayoutHistoryStore'
import { AdminTransaction } from '../AdminTransaction'
import { HistoricalTransactionViewModel } from './HistoricalTransactionViewModel'
import { PayoutsAdminModulePorts } from '../ports'
import { map, filter, switchMap } from 'rxjs/operators'
import { whileActive, shareLatest } from 'utils/rx-util'
import {
  DateRange,
  DateRangeFilter,
} from 'infra/FilterControls/DateRange/DateRangeFilter'
import { FiltersViewModel, FilterItemSource } from 'infra/FiltersViewModel'
import { SelectFilter } from 'infra/FilterControls/Select/SelectFilter'
import { SearchTextViewModel } from 'infra/SearchTextViewModel'
import { TransactionFlag } from '../../TransactionFlag'

/**
 * Filter for historical transactions.
 */
export type AdminTransactionHistoryFilter = {
  completedRange: DateRange
  flags: TransactionFlag[]
}

/**
 * View model for the admin transaction history view.
 */
export class AdminTransactionHistoryViewModel {
  /**
   * Tracks whether the view model is active.
   */
  private readonly _active = new BehaviorSubject<boolean>(false)

  /**
   * Transaction view model list.
   */
  private readonly transactionVms

  /**
   * The query to use.
   */
  private readonly query$ = defer(() =>
    combineLatest([
      this.searchText.debouncedValue$,
      this.filters.appliedFilters$,
    ]),
  ).pipe(
    map(
      ([searchText, filters]): ListHistoricalTransactionsQuery => ({
        searchText,
        dateCompleted: filters.completedRange,
        flags: filters.flags,
      }),
    ),
    shareLatest(),
  )

  /**
   * Fetch pipeline.
   */
  private readonly fetch$ = this.query$.pipe(
    whileActive(this._active),
    filter(() => this.canAccessPayoutsAdministration),
    switchMap((query) =>
      // Ignore errors here as we don't want to cancel the pipeline.
      this.transactionsScroller.fetch(query).catch(() => null),
    ),
  )

  /**
   * The search text.
   */
  readonly searchText = new SearchTextViewModel()

  /**
   * Filters.
   */
  readonly filters = new FiltersViewModel<AdminTransactionHistoryFilter>({
    definitions: {
      completedRange: {
        icon: 'date-completed',
        title: 'Date Paid',
        factory: (key, title) => new DateRangeFilter(key, title),
      },
      flags: {
        icon: 'flags',
        title: 'Flags',
        factory: (key, title) =>
          new SelectFilter(
            key,
            title,
            FilterItemSource.static([
              {
                title: 'Job canceled',
                value: TransactionFlag.JobCanceled,
              },
              {
                title: 'Split auto-adjusted',
                value: TransactionFlag.JobPayoutAutoAdjusted,
              },
            ]),
            true,
          ),
      },
    },
  })

  /**
   * Scroller for transactions.
   */
  readonly transactionsScroller = new InfiniteScrollProvider<
    AdminTransaction,
    ListHistoricalTransactionsQuery
  >({
    limit: 200,
    startFetchTaskAsPending: true,
    fetchItems: (paging, ct, query = {}) =>
      this.adminPayoutHistoryStore.listHistoricalTransactions(
        query,
        paging,
        ct,
      ),
  })

  /**
   * Constructor.
   */
  constructor(
    private readonly adminPayoutHistoryStore: AdminPayoutHistoryStore,
    private readonly adapter: PayoutsAdminModulePorts,
  ) {
    makeObservable(this)
    this.transactionVms = new ViewModelList<
      AdminTransaction,
      HistoricalTransactionViewModel
    >({
      create: (model) => new HistoricalTransactionViewModel(model),
      models: () => this.transactionsScroller.items.slice(),
      activate: (vm) => vm.activate(),
      deactivate: (vm) => vm.deactivate(),
    })

    this.fetch$.subscribe()
  }

  /**
   * Gets the total count of transactions that match the server-based filter.
   */
  @computed
  get transactionsCount() {
    return this.transactionsScroller.total
  }

  /**
   * Whether the user can access the payouts administration.
   */
  @computed
  get canAccessPayoutsAdministration() {
    return this.adapter.getCurrentUser().platformAdmin
  }

  /**
   * The transactions that have been loaded.
   */
  @computed
  get transactions() {
    return this.transactionVms.items
  }

  /**
   * Whether any filters have been applied, including search text.
   */
  @computed
  get filtersApplied() {
    return !!this.searchText.value || this.filters.appliedFilterCount > 0
  }

  /**
   * Activates the view model.
   */
  activate() {
    this._active.next(true)
    this.filters.activate()
    this.transactionVms.activate()
  }

  /**
   * Deactivates the view model.
   */
  deactivate() {
    this.filters.deactivate()
    this.transactionVms.deactivate()
    this._active.next(false)
  }

  /**
   * Clears any search filters.
   */
  @action.bound
  clearFilters() {
    this.searchText.clear()
    this.filters.clearExplicitFilters()
  }

  /**
   * Pages through all transactions and exports all of them as CSV.
   */
  exportCsv = task.resolved(async () => {
    const handle = await this.adapter.downloader.requestDownload({
      suggestedName: 'transaction-history.csv',
      fileTypeDescription: 'CSV file',
      accept: { 'text/csv': ['.csv'] },
    })
    if (!handle) {
      return
    }

    const alert = this.adapter
      .newAlert()
      .withState(AlertState.Pending)
      .saying('Exporting', 'This may take a moment.')
      .show()
    try {
      // Get the query that is used for the current list.
      const query = await firstValueFrom(this.query$)

      // Download the historical transactions, pipe them to the file.
      await this.adminPayoutHistoryStore
        .downloadHistoricalTransactionsAsCsv({
          workspaceIds: query.workspaceIds,
          dateCompleted: query.dateCompleted,
          searchText: query.searchText,
          recipientUserId: query.recipientUserId,
        })
        .then((csv) => handle.download(csv))

      alert
        .withState(AlertState.Success)
        .saying(
          'Export completed!',
          `The transaction history was exported to ${handle.fileName}.`,
        )
        .autoDismiss()
    } catch (err) {
      alert.fromError(err).autoDismiss()
    }
  })
}
